import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { allVideos } from '@/mocks/videos';
import { faqs } from '@/mocks/faqs';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '白云视频',
  url: 'https://www.baiyunvideo.com',
  description: '白云视频汇聚97视频、丝瓜视频同类高清影视资源，最新电影热播电视剧综艺节目独家剧集，在线观看不缓冲。',
  sameAs: [
    'https://www.baiyunvideo.com',
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '白云视频',
  url: 'https://www.baiyunvideo.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.baiyunvideo.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const hotVideos = allVideos.filter((v) => v.isHot).slice(0, 5);
  const newVideos = allVideos.filter((v) => v.isNew);
  const recommendedVideos = allVideos.filter((v) => !v.isHot && !v.isNew).slice(0, 12);

  return (
    <>
      <ScrollToTop />
      <SEO
        title="白云视频 - 97视频与丝瓜视频同类高清影视 | 最新电影热播电视剧全集免费在线"
        description="白云视频汇聚97视频、丝瓜视频同类高清影视资源，最新电影热播电视剧综艺节目独家剧集，在线观看不缓冲。白云视频,97视频,丝瓜视频,蓝莓视频,91在线视频,高清影视,最新电影,热播电视剧,综艺节目,独家剧集,在线观看,免费影视"
        keywords="白云视频,97视频,丝瓜视频,蓝莓视频,91在线视频,高清影视,最新电影,热播电视剧,综艺节目,独家剧集,在线观看,免费影视"
        canonicalUrl="https://www.baiyunvideo.com/"
        schema={{ ...organizationSchema, ...websiteSchema, ...faqSchema }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20theater%20atmosphere%20with%20soft%20golden%20stage%20lighting%2C%20abstract%20geometric%20shapes%2C%20modern%20minimal%20design%2C%20warm%20amber%20and%20deep%20charcoal%20color%20palette%2C%20premium%20entertainment%20aesthetic%2C%20wide%20horizontal%20composition&width=1600&height=800&seq=baiyun-hero-01&orientation=landscape"
            alt="白云视频 - 高清影视在线观看"
            title="白云视频 - 97视频丝瓜视频同类高清影视平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>

          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-background-50 mb-4 leading-tight">
              白云视频
            </h1>
            <p className="text-sm md:text-lg text-background-100/90 mb-3 leading-relaxed max-w-[600px] mx-auto">
              汇聚<strong>97视频</strong>、<strong>丝瓜视频</strong>同类高清影视资源
            </p>
            <p className="text-xs md:text-base text-background-200/80 mb-8 leading-relaxed max-w-[700px] mx-auto">
              最新电影 · 热播电视剧 · 综艺节目 · 独家剧集 — 全部免费在线观看，不缓冲不卡顿
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/movies"
                className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill mr-1.5"></i>开始观影
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-background-50/20 text-background-50 text-sm font-medium rounded-md border border-background-50/30 hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                影视分类
              </h2>
              <p className="text-sm text-foreground-500">探索丰富的影视资源，总有一部适合你</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-4 md:p-5 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Videos Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-1">
                  <i className="ri-fire-line text-accent-500 mr-2"></i>热播推荐
                </h2>
                <p className="text-sm text-foreground-500">大家都在看的热门影视</p>
              </div>
              <Link
                to="/category/movies"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {hotVideos.slice(0, 1).map((video) => (
                <div key={video.id} className="lg:col-span-3">
                  <VideoCard video={video} size="large" />
                </div>
              ))}
              {hotVideos.slice(1).map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals Section */}
        {newVideos.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-1">
                    <i className="ri-sparkling-line text-primary-500 mr-2"></i>最新上线
                  </h2>
                  <p className="text-sm text-foreground-500">刚刚更新的新鲜内容</p>
                </div>
                <Link
                  to="/category/tv-series"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                {newVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Videos Grid */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                精选影视
              </h2>
              <p className="text-sm text-foreground-500">精选高分影视作品，品质保证</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
              {recommendedVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/category/movies"
                className="inline-flex items-center gap-1.5 px-6 py-3 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-md hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部影视 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center p-5 bg-background-50 border border-background-200 rounded-lg">
                <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">20,000+</div>
                <p className="text-xs md:text-sm text-foreground-500">影视资源</p>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200 rounded-lg">
                <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">8大</div>
                <p className="text-xs md:text-sm text-foreground-500">内容分类</p>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200 rounded-lg">
                <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">5000万+</div>
                <p className="text-xs md:text-sm text-foreground-500">活跃用户</p>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200 rounded-lg">
                <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">4K</div>
                <p className="text-xs md:text-sm text-foreground-500">超清画质</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于白云视频，你可能想了解的</p>
            </div>

            <div className="flex flex-col gap-3">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                    aria-expanded={openFaq === faq.id}
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 text-lg flex-shrink-0 ${
                        openFaq === faq.id ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === faq.id && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}